import React, { ReactNode, useRef } from 'react';
import ReactDOM from 'react-dom';
import './GenericFlyoutPanel.scss';

interface GenericFlyoutPanelProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Optional heading shown in the panel header bar. */
  title?: string;
  width?: number;
  minWidth?: number;
}

const GenericFlyoutPanel: React.FC<GenericFlyoutPanelProps> = ({
  open, onClose, children, title, width = 480, minWidth = 320,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const resizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(width);

  const handleMouseDown = (e: React.MouseEvent) => {
    resizing.current = true;
    startX.current = e.clientX;
    startWidth.current = panelRef.current ? panelRef.current.offsetWidth : width;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!resizing.current || !panelRef.current) return;
    let newWidth = startWidth.current - (e.clientX - startX.current);
    newWidth = Math.max(minWidth, newWidth);
    panelRef.current.style.width = `${newWidth}px`;
  };

  const handleMouseUp = () => {
    resizing.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const panel = (
    <div
      className={`generic-flyout-panel${open ? ' open' : ''}`}
      ref={panelRef}
      style={{ width, minWidth, position: 'fixed', top: 0, right: 0, height: '100vh', zIndex: 2000 }}
    >
      <div className="generic-flyout-panel__resize-handle" onMouseDown={handleMouseDown} />
      <div className="generic-flyout-panel__header">
        {title && <span className="generic-flyout-panel__title">{title}</span>}
        <button
          type="button"
          className="generic-flyout-panel__close"
          aria-label="Close"
          onClick={onClose}
        >
          &times;
        </button>
      </div>
      <div className="generic-flyout-panel__content">
        {children}
      </div>
    </div>
  );

  return ReactDOM.createPortal(panel, document.body);
};

export default GenericFlyoutPanel;
